import { useState, useEffect } from 'react'
import { analyticsApi, campaignApi } from '../api'
import type { ScheduledComment, CommentMetrics, Campaign } from '../types'

interface CommentWithMetrics extends ScheduledComment {
  metrics?: CommentMetrics
}

interface CampaignStats {
  campaignId: string
  campaignName: string
  totalComments: number
  averageUpvoteRatio: number
  shadowbanRate: number
}

export function AnalyticsDashboard() {
  const [comments, setComments] = useState<CommentWithMetrics[]>([])
  const [_campaigns, setCampaigns] = useState<Campaign[]>([])
  const [campaignStats, setCampaignStats] = useState<CampaignStats[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [commentsData, campaignsData] = await Promise.all([
        analyticsApi.getComments(),
        campaignApi.list(),
      ])

      setComments(commentsData.comments)
      setCampaigns(campaignsData.campaigns)

      // Load stats for each campaign
      const stats = await Promise.all(
        campaignsData.campaigns.map(async (campaign) => {
          try {
            const data = await analyticsApi.getCampaignStats(campaign.id)
            return {
              campaignId: campaign.id,
              campaignName: campaign.name,
              ...data,
            }
          } catch {
            return {
              campaignId: campaign.id,
              campaignName: campaign.name,
              totalComments: 0,
              averageUpvoteRatio: 0,
              shadowbanRate: 0,
            }
          }
        })
      )

      setCampaignStats(stats)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <div>Loading analytics...</div>
  if (error) return <div style={{ color: 'red' }}>{error}</div>

  const postedComments = comments.filter((c) => c.status === 'posted')
  const totalUpvotes = postedComments.reduce((sum, c) => sum + (c.metrics?.upvotes || 0), 0)
  // const totalDownvotes = postedComments.reduce((sum, c) => sum + (c.metrics?.downvotes || 0), 0)
  const totalReplies = postedComments.reduce((sum, c) => sum + (c.metrics?.replies || 0), 0)
  const avgUpvoteRatio =
    postedComments.length > 0
      ? postedComments.reduce((sum, c) => sum + (c.metrics?.upvoteRatio || 0), 0) / postedComments.length
      : 0

  return (
    <div>
      <h2>Analytics Dashboard</h2>

      {/* Overall Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <div
          style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#666' }}>Total Comments</h3>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold' }}>{postedComments.length}</p>
        </div>
        <div
          style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#666' }}>Total Upvotes</h3>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>
            {totalUpvotes}
          </p>
        </div>
        <div
          style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#666' }}>Total Replies</h3>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#007bff' }}>
            {totalReplies}
          </p>
        </div>
        <div
          style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#666' }}>Avg Upvote Ratio</h3>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold' }}>
            {(avgUpvoteRatio * 100).toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Campaign Stats */}
      <h3 style={{ marginBottom: '15px' }}>Campaign Performance</h3>
      <div style={{ marginBottom: '30px' }}>
        {campaignStats.length === 0 ? (
          <p>No campaign data available.</p>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {campaignStats.map((stat) => (
              <div
                key={stat.campaignId}
                style={{
                  padding: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                }}
              >
                <h4 style={{ margin: '0 0 10px 0' }}>{stat.campaignName}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                  <div>
                    <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '14px' }}>Comments</p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                      {stat.totalComments}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '14px' }}>
                      Avg Upvote Ratio
                    </p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                      {(stat.averageUpvoteRatio * 100).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '14px' }}>
                      Shadowban Rate
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: stat.shadowbanRate > 0.3 ? '#dc3545' : '#28a745',
                      }}
                    >
                      {(stat.shadowbanRate * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Comments */}
      <h3 style={{ marginBottom: '15px' }}>Recent Comments</h3>
      {postedComments.length === 0 ? (
        <p>No posted comments yet.</p>
      ) : (
        <div style={{ display: 'grid', gap: '15px' }}>
          {postedComments.slice(0, 10).map((comment) => (
            <div
              key={comment.id}
              style={{
                padding: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <div style={{ marginBottom: '10px' }}>
                <h4 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>{comment.postTitle}</h4>
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                  r/{comment.postSubreddit} • Posted{' '}
                  {comment.postedAt ? new Date(comment.postedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {comment.metrics && (
                <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
                  <span style={{ color: '#28a745' }}>↑ {comment.metrics.upvotes}</span>
                  <span style={{ color: '#dc3545' }}>↓ {comment.metrics.downvotes}</span>
                  <span style={{ color: '#007bff' }}>💬 {comment.metrics.replies} replies</span>
                  <span>Ratio: {(comment.metrics.upvoteRatio * 100).toFixed(1)}%</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
